import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LgvLoanApp } from '../../data-models/lgv-loan-application.model';
import { LgvUserService } from './lgv-user.service';
import { LgvAuthMgrService } from './lgv-auth-mgr.service';
import { LgvModel } from '../../data-models/longevity.model';
import {
    BehaviorSubject,
    EMPTY,
    filter,
    first,
    map,
    Observable,
    of,
    take,
    tap
} from 'rxjs';
import { environment as env } from '../../../environments/environment';
import { LgvConstants, TaskStatus } from '../utility/lgv-constants';
import {
    NameValueNumericPair,
    NameValuePair
} from '../../data-models/name-value-pair';
import { JsonLoaderService } from './json-loader.service';
import { LgvMockDataService } from './lgv-mock-data.service';
import { LgvFormlyModel } from '../../data-models/formly.model';
import { LgvFormUtilityService } from './lgv-form-utility.service';
import { LgvClientService } from './lgv-client.service';
import { LgvPricingModel } from '../../features/pricing/models/pricing.model';

@Injectable({
    providedIn: 'root'
})
export class LgvLoanApplicationService {
    // lookups
    _taskSets: LgvLoanApp.TaskSet[] = [];
    taskSets$ = new BehaviorSubject(this._taskSets);
    _taskItems: LgvLoanApp.TaskItem[] = [];
    taskItems$ = new BehaviorSubject(this._taskItems);
    _taskItemsPublic: LgvLoanApp.TaskItem[] = [];
    taskItemsPublic$ = new BehaviorSubject(this._taskItems);
    _taskTypes: LgvLoanApp.GenericType[] = [];
    taskTypes$ = new BehaviorSubject(this._taskTypes);
    _assignableTaskTypes: LgvLoanApp.GenericType[] = [];
    assignableTaskTypes$ = new BehaviorSubject(this._assignableTaskTypes);
    _customFields: LgvLoanApp.CustomField[] = [];
    customFields$ = new BehaviorSubject(this._customFields);
    creditReportTypes$ = new BehaviorSubject<LgvLoanApp.GenericType[]>([]);
    creditReportFees$ = new BehaviorSubject<LgvLoanApp.CreditReportFeeInfo>(
        null
    );
    clientLoanApplication$ = new BehaviorSubject([]);

    taskItemsForType$(taskTypeId: string): Observable<LgvLoanApp.TaskItem[]> {
        if (!taskTypeId) {
            return of([]);
        }
        return this.taskItems$.pipe(
            filter(f => f !== null),
            take(1),
            //tap(t => { console.log('t'); console.log(t); }),
            map(arr => {
                if (
                    taskTypeId.toUpperCase() ==
                    'e599535a-03d2-ee11-85f7-6045bd020c58'.toUpperCase()
                ) {
                    return arr.filter(x => x.name == 'Credit Report');
                } else {
                    return arr.filter(
                        x =>
                            x.type.id.toUpperCase() === taskTypeId.toUpperCase()
                    );
                }
            })
        );
    }

    taskItemsForSet$(taskSetId: string): Observable<LgvLoanApp.TaskItem[]> {
        if (!taskSetId) {
            return of([]);
        }
        return this.taskSets$.pipe(
            filter(f => f !== null),
            take(1),
            //tap(t => { console.log('t'); console.log(t); }),
            map(arr =>
                arr.find(x => x.id.toUpperCase() === taskSetId.toUpperCase())
            ),
            map(ts => ts.taskItems)
        );
    }

    taskItemsForSet(taskSetId: string): LgvLoanApp.TaskItem[] {
        if (!taskSetId || !this.taskSets$.value) {
            return [];
        }

        let targetTaskSet = this.taskSets$.value.find(
            x => x.id.toUpperCase() == taskSetId.toUpperCase()
        );
        return targetTaskSet ? targetTaskSet.taskItems : [];
    }

    getTaskById$(id: string): Observable<LgvLoanApp.Task> {
        return this.taskItems$.pipe(
            filter(f => f !== null),
            take(1),
            //tap(t => { console.log('t'); console.log(t); }),
            map(arr => arr.find(x => x.id.toUpperCase() === id.toUpperCase()))
        );
    }

    getTypeById$(id: string): Observable<LgvLoanApp.GenericType> {
        return this.taskTypes$.pipe(
            filter(f => f !== null),
            take(1),
            //tap(t => { console.log('t'); console.log(t); }),
            map(arr => arr.find(x => x.id.toUpperCase() === id.toUpperCase()))
        );
    }

    //Loan App - Move to FormlyUtilitySvc???
    // _loanAppModel: any = this.getDefaultLoanAppJsonModel();
    // loanAppModel$ = new BehaviorSubject(this._loanAppModel);

    constructor(
        private http: HttpClient,
        private lgvUserSvc: LgvUserService,
        private lgvAuthMgrSvc: LgvAuthMgrService,
        private jsonLoader: JsonLoaderService,
        private lgvClientSvc: LgvClientService
    ) {
        // only load the lookups for
        this.lgvAuthMgrSvc.IsAuthenticated$.pipe(
            filter(f => f !== null)
        ).subscribe(isAuthenticated => {
            if (isAuthenticated && this.taskSets$.value?.length === 0) {
                this.initLookups();
            }
        });

        this.fetchTaskItemsPublic()
            .pipe(take(1))
            .subscribe(x => {
                this.taskItemsPublic$.next(x);
            });

        // this.lgvFormlyUtilitySvc.formDataModel$.next(this.getDefaultLoanAppJsonModel());
    }

    // internal use (for agents, etc...)
    public startLoanApplication(
        agentId: string,
        model: LgvLoanApp.LoanApplicationDetail
    ) {
        const url = `${env.LgvApiBaseUrl}/loanapplications`;
        return this.http.post<any>(url, model).pipe(first());
    }

    //#region worker methods
    public buildLoanAppModel(
        opportunity: LgvModel.Opportunity,
        property: LgvModel.Property,
        clients: LgvModel.ClientInfo[],
        loan: LgvModel.Loan,
        agent: LgvModel.AgentDTO,
        assistant: LgvModel.UserDTO,
        processor: LgvModel.UserDTO,
        rawBorrowersModel: LgvLoanApp.Borrower[] = null,
        rawLoanModel: LgvLoanApp.LoanApplicationDetail = null,
        purpose: string = null,
        state: string = null
    ): LgvLoanApp.LoanApplicationDetail {
        /*
         * Questions:
         *  Do they need an opportunity to get here?  Any other path?
         */

        let model: LgvLoanApp.LoanApplicationDetail = {
            updatedBy: null,
            id: null,
            loanInfo: null,
            tasks: [],
            company: null
        };

        // Pull values from opportunity if there, otherwise use the loan info
        let oi = opportunity?.opportunityInfo ?? null;
        let li = loan?.loanInfo ?? null;

        let loanAmt =
            oi?.loanAmount ?? loan?.loanAnalysis?.estimatedBalance ?? null;
        if (!!loanAmt) {
            loanAmt = +loanAmt.toFixed(2);
        }

        if (rawLoanModel) {
            model.loanInfo = rawLoanModel;
            if (rawBorrowersModel) {
                model.loanInfo.borrowers = rawBorrowersModel;
            }
        } else {
            model.loanInfo = {
                id: li?.id ?? null,
                loanNumber: null,
                loanAmount: loanAmt,
                appraisalValue: li?.appraisedValue ?? null,
                assignments: {
                    loanAssistant: null,
                    loanOfficer: null,
                    loanProcessor: null
                },
                borrowers: rawBorrowersModel ?? [],
                purpose: {
                    name: !!purpose
                        ? purpose
                        : oi?.loanPurpose ?? !!li
                        ? 'Refinance'
                        : 'Purchase'
                },
                loanType: {
                    name: oi?.loanTypeName ?? loan?.loanType?.name ?? null
                },
                occupancy: null,
                loanStatus: null,
                loanTerm: oi?.loanTermMonths ?? li?.loanTerm ?? null,
                noteRate: oi?.matchedRate ?? null,
                subjectPropertyAddress: null
            };
        }

        if (!!property) {
            let pi = property.propertyInfo;
            model.loanInfo.subjectPropertyAddress = {
                propertyId: pi.id,
                street: pi.address,
                city: pi.city,
                state: pi.state,
                zipCode: pi.zip,
                county: pi.county
            };
        } else {
            model.loanInfo.subjectPropertyAddress = {
                state: state
            };
        }

        if (!!clients && clients.length > 0) {
            let borrowers: LgvLoanApp.Borrower[] = [];

            for (let c of clients) {
                borrowers.push({
                    clientId: c.id ?? null,
                    firstName: c.firstName,
                    lastName: c.lastName,
                    contacts: {
                        email: c.email,
                        mobilePhone: c.mobilePhone
                    }
                });
            }
            model.loanInfo.borrowers = borrowers;
        }

        if (!!agent) {
            let ai = agent.agentInfo;
            model.loanInfo.assignments.loanOfficer = {
                id: ai.id,
                email: ai.email,
                firstName: ai.firstName,
                lastName: ai.lastName,
                name: ai.firstName + ' ' + ai.lastName
            };

            if (!!agent.company) {
                model.company = {
                    name: agent.company.name,
                    id: agent.company.id,
                    code: agent.company.companyCode
                };
            }
        }

        if (!!assistant) {
            model.loanInfo.assignments.loanAssistant = {
                id: assistant.id,
                email: assistant.email,
                firstName: assistant.firstName,
                lastName: assistant.lastName,
                name: assistant.firstName + ' ' + assistant.lastName
            };
        }

        if (!!processor) {
            model.loanInfo.assignments.loanProcessor = {
                id: processor.id,
                email: processor.email,
                firstName: processor.firstName,
                lastName: processor.lastName,
                name: processor.firstName + ' ' + processor.lastName
            };
        }

        return model;
    }

    public buildLoanInfoModelFromPricingSearch(
        searchModel: LgvPricingModel.ProductSearch,
        product: LgvPricingModel.Product
    ): LgvLoanApp.ApplicationLoanInfo {
        return {
            id: null,
            loanNumber: null,
            loanAmount: searchModel?.loanInformation?.baseLoanAmount,
            appraisalValue: searchModel?.propertyInformation.appraisedValue,
            assignments: {
                loanAssistant: null,
                loanOfficer: null,
                loanProcessor: null
            },
            borrowers: [],
            purpose: { name: searchModel?.loanInformation.loanPurpose },
            loanType: { name: product.loanType },
            occupancy: null,
            loanStatus: null,
            loanTerm: +product.loanTerm,
            noteRate: product.rate,

            subjectPropertyAddress: {
                state: searchModel?.propertyInformation.state,
                zipCode: searchModel?.propertyInformation.zipCode,
                county: searchModel?.propertyInformation.county
            }
        };
    }

    public buildTaskStatusList(
        tasks: LgvLoanApp.Task[]
    ): NameValueNumericPair[] {
        if (!tasks) {
            return null;
        }

        return [
            {
                name: 'Accepted',
                value: tasks.filter(
                    t => t.status.id.toUpperCase() === TaskStatus.Accepted
                ).length
            },
            {
                name: 'Requested',
                value: tasks.filter(
                    t => t.status.id.toUpperCase() === TaskStatus.Requested
                ).length
            },
            {
                name: 'Rejected',
                value: tasks.filter(
                    t => t.status.id.toUpperCase() === TaskStatus.Rejected
                ).length
            },
            {
                name: 'Uploaded',
                value: tasks.filter(
                    t => t.status.id.toUpperCase() === TaskStatus.Uploaded
                ).length
            },
            {
                name: 'Submitted',
                value: tasks.filter(
                    t => t.status.id.toUpperCase() === TaskStatus.Submitted
                ).length
            }
        ];
    }

    //#endregion worker methods

    public initLookups() {
        if (!this.lgvAuthMgrSvc.IsClient) {
            this.fetchTaskSets()
                .pipe(take(1))
                .subscribe(x => {
                    this.taskSets$.next(x);
                });

            this.fetchTaskItems()
                .pipe(take(1))
                .subscribe(x => {
                    this.taskItems$.next(x);
                });

            this.fetchTaskTypes()
                .pipe(take(1))
                .subscribe(taskTypes => {
                    this.taskTypes$.next(taskTypes);
                    this.assignableTaskTypes$.next(
                        taskTypes.filter(
                            taskType => taskType.name !== 'Disclosure'
                        )
                    );
                });

            this.getCreditReportTypes()
                .pipe(take(1))
                .subscribe(creditReportTypes =>
                    this.creditReportTypes$.next(creditReportTypes)
                );

            this.getCreditReportFees()
                .pipe(take(1))
                .subscribe(creditReportFees =>
                    this.creditReportFees$.next(creditReportFees)
                );
        }
    }

    //#region Lookups

    public fetchLoanAppActivities(
        appId: string,
        searchText = null
    ): Observable<LgvLoanApp.LoanAppActivityLog[]> {
        const clientQuery = this.lgvAuthMgrSvc.IsClient
            ? `clientId=${this.lgvClientSvc.ClientID$.value}`
            : '';
        const searchTextQuery = searchText ? `searchText=${searchText}` : '';
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${appId}/audit?${clientQuery}&${searchTextQuery}`;
        return this.http.get<LgvLoanApp.LoanAppActivityLog[]>(bldUrl);
    }

    public fetchTaskSets(): Observable<LgvLoanApp.TaskSet[]> {
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/task-sets`;
        return this.http.get<LgvLoanApp.TaskSet[]>(bldUrl);
    }

    public fetchTaskItems(): Observable<LgvLoanApp.TaskItem[]> {
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/task-items`;
        return this.http.get<LgvLoanApp.TaskItem[]>(bldUrl);
    }

    public fetchTaskItemsPublic(): Observable<LgvLoanApp.TaskItem[]> {
        let bldUrl = `${env.LgvApiBaseUrl}/types/loan-application-task-items`;
        return this.http.get<LgvLoanApp.TaskItem[]>(bldUrl);
    }

    public fetchTaskTypes(): Observable<LgvLoanApp.GenericType[]> {
        let bldUrl = `${env.LgvApiBaseUrl}/types/loan-application-task-types`;
        return this.http.get<LgvLoanApp.GenericType[]>(bldUrl);
    }

    public fetchCustomFields(loanAppId): Observable<LgvLoanApp.CustomField[]> {
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/custom-fields?loanApplicationId=${loanAppId}`;
        return this.http.get<LgvLoanApp.CustomField[]>(bldUrl);
    }

    public buildCustomField(code: string, val: string) {
        let rtnVal = null;

        if (this.customFields$.value?.length > 0) {
            let cf = this.customFields$.value.find(
                x => x.code.toUpperCase() === code.toUpperCase()
            );
            if (cf) {
                rtnVal = structuredClone(cf);
                if (val === 'Yes' || val === 'true') {
                    rtnVal.value = true;
                } else if (val === 'No' || val === 'false') {
                    rtnVal.value = false;
                } else {
                    rtnVal.value = val;
                }
            }
        }

        return rtnVal;
    }

    getLoanAppSchemaFromLOS(los: string) {
        if (los === 'MeridianLink') {
            return this.jsonLoader.meridianLinkLoanAppJson$.value;
        } else {
            return this.jsonLoader.lendingPadLoanAppJson$.value;
        }
    }

    public fetchVaultDocuments(
        loanApplicationId: string,
        clientId?: string
    ): Observable<LgvLoanApp.VaultDocument[]> {
        let url = `${env.LgvApiBaseUrl}/loanapplications/${loanApplicationId}/vault/documents`;
        if (clientId) {
            url += `?clientId=${clientId}`;
        } else if (this.lgvAuthMgrSvc.IsClient) {
            url += `?clientId=${this.lgvClientSvc.ClientID$.value}`;
        }
        return this.http.get<LgvLoanApp.VaultDocument[]>(url);
    }

    //#endregion Lookups

    //#region tasks
    public addTask(model: any, appId: string): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${appId}/tasks`;
        return this.http.post<any>(bldUrl, model).pipe(first());
    }

    public updateTask(
        model: any,
        taskId: string,
        appId: string
    ): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${appId}/tasks/${taskId}`;
        return this.http.put<any>(bldUrl, model).pipe(first());
    }

    public deleteTask(taskId: string, appId: string): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${appId}/tasks/${taskId}`;
        return this.http.delete<any>(bldUrl).pipe(first());
    }

    public isForbiddenDuplicateTaskItems(taskItemId: string): boolean {
        const forbiddenDuplicateTaskItems = [
            '96930FD5-66DB-EE11-85F7-00224802BEB3'
        ];
        return forbiddenDuplicateTaskItems.includes(taskItemId.toUpperCase());
    }

    //#endregion tasks

    //#region Vault
    public deleteVaultDocument(
        loanApplicationId: string,
        documentId: string,
        clientId?: string
    ): Observable<any> {
        let url = `${env.LgvApiBaseUrl}/loanapplications/${loanApplicationId}/vault/documents/${documentId}`;
        if (clientId) {
            url += `?clientId=${clientId}`;
        } else if (this.lgvAuthMgrSvc.IsClient) {
            url += `?clientId=${this.lgvClientSvc.ClientID$.value}`;
        }
        return this.http.delete<any>(url);
    }

    fetchVaultDocument(
        loanApplicationId: string,
        documentId: string
    ): Observable<Blob> {
        const url = `${env.LgvApiBaseUrl}/loanapplications/${loanApplicationId}/vault/documents/${documentId}`;
        return this.http.get(url, { responseType: 'blob' });
    }

    // Update vault document properties (e.g., name or description)
    updateVaultDocument(
        loanApplicationId: string,
        documentId: string,
        document: LgvLoanApp.VaultDocument
    ): Observable<any> {
        const url = `${env.LgvApiBaseUrl}/loanapplications/${loanApplicationId}/vault/documents/${documentId}`;
        return this.http.post(url, document);
    }

    //#endregion Vault

    public fetchLoanApplications(clientId: string): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications?top=25&clientId=${clientId}`;
        return this.http
            .get<any>(bldUrl)
            .pipe(tap(la => this.clientLoanApplication$.next(la?.value)));
    }

    public fetchThisLoanApp(
        id: string,
        clientId?: string
    ): Observable<LgvLoanApp.LoanApplicationDetail> {
        if (!id) {
            return EMPTY;
        }
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${id}`;
        if (clientId) {
            bldUrl = bldUrl + `?clientId=${clientId}`;
        }

        // custom fields are needed by clients and admin / agents
        this.fetchCustomFields(id)
            .pipe(take(1))
            .subscribe(x => {
                this.customFields$.next(x);
            });
        return this.http.get<LgvLoanApp.LoanApplicationDetail>(bldUrl);
    }

    public deleteThisLoanApp(appId: string): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${appId}`;
        return this.http.delete<any>(bldUrl).pipe(first());
    }

    public restoreThisLoanApp(appId: string): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${appId}/restore`;
        return this.http.patch<any>(bldUrl, null).pipe(first());
    }

    public linkEmailAttachmentToTask(
        userId: string,
        loanAppId: string,
        taskId: string,
        emailId: string,
        attachmentId: string
    ): Observable<any> {
        let model = {
            userId: userId,
            emailId: emailId,
            attachmentId: attachmentId
        };
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${loanAppId}/tasks/${taskId}/emails/${emailId}/attachments/${attachmentId}/upload`;
        return this.http.post<any>(bldUrl, model);
    }

    public getDisclosureTask(
        loanAppId: string,
        taskId: string
    ): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${loanAppId}/tasks/${taskId}/disclosure`;
        return this.http.get<any>(bldUrl);
    }

    //#region credit report
    public submitCreditReport(requestBody: any, loanAppId, taskId, clientId) {
        if (clientId) {
            clientId = '?clientId=' + clientId;
        } else {
            clientId = '';
        }
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${loanAppId}/tasks/${taskId}/credit-report/order${clientId}`;
        return this.http.post<any>(bldUrl, requestBody);
    }

    public getCreditReportTypes() {
        let bldUrl = `${env.LgvApiBaseUrl}/types/credit-report-types`;
        return this.http.get<any>(bldUrl);
    }

    public getCreditReportFees() {
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/credit-report/fees`;
        return this.http.get<any>(bldUrl);
    }
    //#endregion credit report
}
